import React from "react";
import { Form, Radio, Popup, Icon, Button } from "semantic-ui-react";
import {
  pencilerRoles,
  urbancanvasRoles,
  RoleClass,
  viewerRoleConfigEnabledDBs
} from "../../../../config/config";
import { User } from "../../../../types/users";
import { textInputRender } from "../../../../utils/components";
import { validateEmailString, validateUsedInOrganization } from "../../../../utils/formValidation";
import { CustomFormFieldProps } from "../../../common/Form";
import FormModal from "../../../common/FormModal";

type InviteUserToOrganizationFormProps = {
  existingUsers: User[];
  canInviteToPenciler: boolean;
  canInviteToUrbancanvas: boolean;
  disabled: boolean;
  onSubmit: (invitee: {
    firstName: string;
    lastName: string;
    email: string;
    roleCode: string;
  }) => void;
  regionDB?: string;
};

/**
 * Modal with form for inviting users to join the account/organization.
 * Errors must be handled via it's father component.
 */
class InviteUserToOrganizationForm extends React.PureComponent<InviteUserToOrganizationFormProps> {
  buildRoleField = (
    label: string,
    value: string,
    infoHeader: string,
    infoContent: React.ReactFragment,
    index: number
  ): CustomFormFieldProps => ({
    name: "roleCode",
    type: "radio",
    value: value,
    required: true,
    render: ({ input }) => (
      <>
        {index === 0 && (
          <label style={{ display: "block", marginBottom: "8px" }}>
            <b>User Role (choose one):</b>
          </label>
        )}
        <Form.Field>
          <Radio
            id={input.name + input.value}
            label={label}
            name="roleCode"
            checked={input.checked}
            {...input}
          />
          {infoHeader && infoContent && (
            <Popup
              hoverable
              flowing
              position="right center"
              trigger={<Icon link inverted color="blue" name="info circle" />}
              header={infoHeader}
              content={infoContent}
            />
          )}
        </Form.Field>
      </>
    )
  });

  rolesFields = () => {
    const mapBuildRoleField = (availableRoles: RoleClass[]) =>
      availableRoles.map((role, index) =>
        this.buildRoleField(
          role.displayName,
          role.code,
          role.inviteUserPopup.header,
          role.inviteUserPopup.content,
          index
        )
      );
    const pencilerAvailableRoles = pencilerRoles.availableRoles;
    const urbancanvasAvailableRoles = urbancanvasRoles.availableRoles;
    const { canInviteToPenciler, canInviteToUrbancanvas } = this.props;
    let roles: CustomFormFieldProps[] = [];
    if (canInviteToPenciler) {
      roles = roles.concat(mapBuildRoleField(pencilerAvailableRoles));
    }
    if (canInviteToUrbancanvas) {
      roles = roles.concat(mapBuildRoleField(urbancanvasAvailableRoles));
    }
    if (!viewerRoleConfigEnabledDBs.includes(this.props.regionDB ?? "")) {
      roles = roles.filter(role => role.value !== "URBANCANVAS_VIEWER");
    }
    return roles;
  };

  fields: CustomFormFieldProps[] = [
    {
      name: "email",
      render: textInputRender("Email", "example@email.com", true),
      validators: [validateEmailString, validateUsedInOrganization(this.props.existingUsers)],
      type: "email",
      required: true
    },
    ...this.rolesFields()
  ];

  render = () => (
    <FormModal
      fields={this.fields}
      onSubmit={this.props.onSubmit}
      size="mini"
      submitText={"Send invite"}
      modalHeader={"Invite user to your UrbanSim account"}
      modalTrigger={onClick => (
        <Button
          style={{ marginBottom: "1%" }}
          disabled={
            !(this.props.canInviteToPenciler || this.props.canInviteToUrbancanvas) ||
            this.props.disabled
          }
          onClick={onClick}
        >
          Add user
        </Button>
      )}
    />
  );
}

export default InviteUserToOrganizationForm;
